import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Trash2, ChevronDown, Check } from 'lucide-react';
import { Label } from '../ui/label';

/**
 * GradientEditor — Figma-style gradient editor.
 *
 * Props:
 *   value: { type, colors, positions, opacities, angle } — current gradient config
 *   onChange: (newGradient) => void — called on any change
 */

const DEFAULT_GRADIENT = {
  type: 'linear',
  colors: ['#6366f1', '#ec4899'],
  positions: [0, 100],
  opacities: [1, 1],
  angle: 90,
};

export const buildGradientCSS = (gradient) => {
  const { type = 'linear', colors = [], positions = [], opacities = [], angle = 90 } = gradient || {};
  const stops = colors.map((c, i) => {
    const opacity = opacities[i] ?? 1;
    const pos = positions[i] ?? (i === 0 ? 0 : 100);
    // Convert hex + opacity to rgba
    const hex = c.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${opacity}) ${pos}%`;
  }).join(', ');

  if (type === 'radial' || type === 'diamond') {
    return `radial-gradient(circle, ${stops})`;
  }
  if (type === 'angular') {
    return `conic-gradient(from ${angle}deg, ${stops})`;
  }
  return `linear-gradient(${angle}deg, ${stops})`;
};

const hexToRgba = (hex, opacity) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity ?? 1})`;
};

const GRADIENT_TYPES = [
  { value: 'linear',  label: 'Linear' },
  { value: 'radial',  label: 'Radial' },
  { value: 'angular', label: 'Angular' },
  { value: 'diamond', label: 'Diamond' },
];

const GradientEditor = ({ value, onChange }) => {
  const gradient = { ...DEFAULT_GRADIENT, ...value };
  const { type, colors, positions, opacities, angle } = gradient;

  const [selectedStop, setSelectedStop] = useState(0);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const barRef = useRef(null);
  const draggingStop = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setTypeDropdownOpen(false);
      }
    };
    if (typeDropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [typeDropdownOpen]);

  const update = useCallback((patch) => {
    onChange({ ...gradient, ...patch });
  }, [gradient, onChange]);

  // ─── Stop management ───────────────────────────────────────────────────────

  const addStop = useCallback((e) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const pos = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    // Find the two surrounding stops and interpolate color
    const sortedIdxs = [...positions.keys()].sort((a, b) => positions[a] - positions[b]);
    let color = colors[0];
    for (let i = 0; i < sortedIdxs.length - 1; i++) {
      const a = sortedIdxs[i], b = sortedIdxs[i + 1];
      if (pos >= positions[a] && pos <= positions[b]) {
        // Just use the color of the closer stop
        color = (pos - positions[a]) < (positions[b] - pos) ? colors[a] : colors[b];
        break;
      }
    }
    const newColors = [...colors, color];
    const newPositions = [...positions, pos];
    const newOpacities = [...opacities, 1];
    const newIdx = newColors.length - 1;
    update({ colors: newColors, positions: newPositions, opacities: newOpacities });
    setSelectedStop(newIdx);
  }, [colors, positions, opacities, update]);

  const removeStop = useCallback((i) => {
    if (colors.length <= 2) return;
    const newColors = colors.filter((_, j) => j !== i);
    const newPositions = positions.filter((_, j) => j !== i);
    const newOpacities = opacities.filter((_, j) => j !== i);
    update({ colors: newColors, positions: newPositions, opacities: newOpacities });
    setSelectedStop(Math.min(i, newColors.length - 1));
  }, [colors, positions, opacities, update]);

  const updateStop = useCallback((i, patch) => {
    const newColors = [...colors];
    const newPositions = [...positions];
    const newOpacities = [...opacities];
    if (patch.color !== undefined) newColors[i] = patch.color;
    if (patch.position !== undefined) newPositions[i] = Math.max(0, Math.min(100, patch.position));
    if (patch.opacity !== undefined) newOpacities[i] = Math.max(0, Math.min(1, patch.opacity));
    update({ colors: newColors, positions: newPositions, opacities: newOpacities });
  }, [colors, positions, opacities, update]);

  // ─── Drag logic ────────────────────────────────────────────────────────────

  const handleStopMouseDown = useCallback((e, i) => {
    e.stopPropagation();
    e.preventDefault();
    draggingStop.current = i;
    setSelectedStop(i);

    const onMove = (me) => {
      if (draggingStop.current === null || !barRef.current) return;
      const rect = barRef.current.getBoundingClientRect();
      const pos = Math.round(((me.clientX - rect.left) / rect.width) * 100);
      updateStop(draggingStop.current, { position: pos });
    };
    const onUp = () => {
      draggingStop.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [updateStop]);

  // Clamp selectedStop to valid range if stops removed
  useEffect(() => {
    if (selectedStop >= colors.length) setSelectedStop(colors.length - 1);
  }, [colors.length, selectedStop]);

  const sel = selectedStop < colors.length ? selectedStop : 0;
  const cssPreview = buildGradientCSS(gradient);

  const showAngle = type === 'linear' || type === 'angular';

  return (
    <div className="space-y-4">

      {/* ── 1. Type dropdown ──────────────────────────────────────── */}
      <div>
        <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
          Gradient Type
        </Label>
        <div className="relative" ref={dropdownRef}>
          {/* Trigger */}
          <button
            onClick={() => setTypeDropdownOpen((o) => !o)}
            className="w-full h-9 px-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white hover:border-purple-400 hover:ring-2 hover:ring-purple-100 transition-all text-sm font-medium text-gray-800"
          >
            <span className="capitalize">{type}</span>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${typeDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Menu */}
          {typeDropdownOpen && (
            <div className="absolute z-50 mt-1 w-full rounded-xl overflow-hidden shadow-xl border border-gray-800/20"
              style={{ background: '#1e1e2e' }}>
              {GRADIENT_TYPES.map(({ value: t, label }) => {
                const isSelected = type === t;
                return (
                  <button
                    key={t}
                    onClick={() => { update({ type: t }); setTypeDropdownOpen(false); }}
                    className={`
                      w-full px-3 py-2 flex items-center justify-between text-sm transition-colors
                      ${isSelected
                        ? 'bg-purple-600/30 text-purple-200'
                        : 'text-gray-300 hover:bg-white/10'}
                    `}
                  >
                    <span className="font-medium">{label}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-purple-400" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── 2. Gradient bar with draggable stops ─────────────────── */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Stops</Label>
          <span className="text-[9px] text-gray-400">Click bar to add · drag to move</span>
        </div>

        {/* Bar + handles container — paddingBottom reserves room for the handles */}
        <div className="relative" style={{ paddingBottom: 16 }}>
          {/* Checkerboard transparency background */}
          <div
            className="h-8 rounded-xl cursor-crosshair relative overflow-visible border border-gray-200 shadow-sm"
            style={{ background: 'repeating-conic-gradient(#d1d5db 0% 25%, #fff 0% 50%) 0 0 / 10px 10px' }}
            ref={barRef}
            onClick={addStop}
          >
            {/* Gradient colour overlay */}
            <div className="absolute inset-0 rounded-xl" style={{ background: cssPreview }} />
          </div>

          {/* Stop handles rendered below the bar */}
          {colors.map((color, i) => {
            const pos = positions[i] ?? 0;
            return (
              <div
                key={i}
                style={{ position: 'absolute', bottom: 0, left: `calc(${pos}% - 8px)`, zIndex: 10 }}
                onMouseDown={(e) => handleStopMouseDown(e, i)}
              >
                {/* Diamond-shaped handle pointing up at the bar */}
                <div
                  className={`
                    w-4 h-4 rounded-full border-2 shadow-md cursor-grab active:cursor-grabbing
                    transition-transform duration-100
                    ${sel === i ? 'border-purple-600 scale-125 shadow-purple-200' : 'border-white scale-100'}
                  `}
                  style={{ background: hexToRgba(color, opacities[i] ?? 1) }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 3. Selected stop controls ─────────────────────────────── */}
      <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 space-y-3">

        {/* Row 1: Color picker + hex input + delete */}
        <div>
          <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
            Color
          </Label>
          <div className="flex items-center gap-2">
            {/* Colour swatch — clicking opens native colour picker */}
            <label className="shrink-0 cursor-pointer">
              <div
                className="w-8 h-8 rounded-lg border-2 border-white shadow ring-1 ring-gray-200 transition-transform hover:scale-105"
                style={{ background: hexToRgba(colors[sel] || '#000000', opacities[sel] ?? 1) }}
              />
              <input
                type="color"
                value={colors[sel] || '#000000'}
                onChange={(e) => updateStop(sel, { color: e.target.value })}
                className="sr-only"
              />
            </label>

            {/* Hex code */}
            <div className="flex items-center gap-1 flex-1 h-8 px-2.5 rounded-lg border border-gray-200 bg-white focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100 transition-all">
              <span className="text-gray-400 text-xs font-mono select-none">#</span>
              <input
                type="text"
                value={(colors[sel] || '#000000').replace('#', '').toUpperCase()}
                onChange={(e) => {
                  const hex = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                  if (hex.length === 6) updateStop(sel, { color: `#${hex}` });
                }}
                className="flex-1 bg-transparent text-xs font-mono text-gray-800 outline-none w-0"
                maxLength={6}
                spellCheck={false}
              />
            </div>

            {/* Delete stop */}
            <button
              onClick={() => removeStop(sel)}
              disabled={colors.length <= 2}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Remove stop"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Row 2: Position + Opacity */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
              Position %
            </Label>
            <input
              type="number"
              min={0}
              max={100}
              value={positions[sel] ?? 0}
              onChange={(e) => updateStop(sel, { position: Number(e.target.value) })}
              className="w-full h-8 px-2.5 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all text-gray-800 font-medium"
            />
          </div>
          <div>
            <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5">
              Opacity
            </Label>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              value={Math.round((opacities[sel] ?? 1) * 100) / 100}
              onChange={(e) => updateStop(sel, { opacity: Number(e.target.value) })}
              className="w-full h-8 px-2.5 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all text-gray-800 font-medium"
            />
          </div>
        </div>
      </div>

      {/* ── 4. Angle (linear / angular only) ─────────────────────── */}
      {showAngle && (
        <div className="space-y-1.5">
          <Label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">
            Angle
          </Label>
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={angle ?? 90}
            onChange={(e) => update({ angle: Number(e.target.value) })}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-purple-600"
            style={{
              backgroundImage: `linear-gradient(to right, #7c3aed ${((angle ?? 90) / 360) * 100}%, #e5e7eb ${((angle ?? 90) / 360) * 100}%)`,
            }}
          />
          {/* Numeric value displayed below the slider — no number input */}
          <p className="text-center text-xs font-semibold text-gray-700 tabular-nums">
            {angle ?? 90}°
          </p>
        </div>
      )}

    </div>
  );
};

export default GradientEditor;
